import React, { useState, useEffect } from 'react';
import { Flame, ShoppingBag } from 'lucide-react';
import { trendingPicks } from '../data/trendingPicks';
import { resolveImageUrl } from '../lib/media';

const WHATSAPP_NUMBER = '5016735717';

const TrendingPicks = ({ language }) => {
  const [items, setItems] = useState(trendingPicks);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/picks`)
      .then((res) => res.json())
      .then((data) => {
        if (active && Array.isArray(data) && data.length > 0) {
          setItems(
            data.map((p) => ({
              image: p.image,
              name: { en: p.name_en, es: p.name_es },
              tag: { en: p.tag_en, es: p.tag_es },
              price: p.price || ''
            }))
          );
        }
      })
      .catch(() => {
        // Keep the static fallback list if the API is unavailable
      });
    return () => {
      active = false;
    };
  }, []);

  const picks = items.map((p) => ({
    image: p.image,
    name: p.name[language] || p.name.en,
    tag: p.tag[language] || p.tag.en,
    price: p.price || ''
  }));

  const orderOnWhatsApp = (pick) => {
    const priceText = pick.price ? ` (${pick.price})` : '';
    const imgUrl = resolveImageUrl(pick.image);
    const msg =
      language === 'en'
        ? `Hi Athena's! I'd like to order this item: ${pick.name}${priceText}. Is it available?\n\nItem photo: ${imgUrl}`
        : `¡Hola Athena's! Me gustaría ordenar este artículo: ${pick.name}${priceText}. ¿Está disponible?\n\nFoto del artículo: ${imgUrl}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Duplicate list for seamless infinite scroll
  const loop = [...picks, ...picks];

  return (
    <section className="py-16 bg-[#1e1919] overflow-hidden" data-testid="trending-picks-section">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8b1538] rounded-full mb-4">
            <Flame className="w-4 h-4 text-[#fad24b]" />
            <span className="text-white text-sm font-bold uppercase tracking-wide">
              {language === 'en' ? "This Week's Picks" : 'Selección de la Semana'}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {language === 'en' ? "This Week's Trending Picks" : 'Tendencias de Esta Semana'}
          </h2>
          <p className="text-[#bbb5ae] text-base">
            {language === 'en'
              ? 'Tap "Order on WhatsApp" on any item and we\'ll get it for you'
              : 'Toca "Ordenar por WhatsApp" en cualquier artículo y lo conseguimos para ti'}
          </p>
        </div>
      </div>

      {/* Rotating strip */}
      <div className="relative w-full">
        <div className="marquee-track gap-6 px-3">
          {loop.map((pick, index) => (
            <div
              key={index}
              data-testid={`trending-pick-${index}`}
              className="group relative flex-shrink-0 w-56 rounded-2xl overflow-hidden bg-[#2a2323] border-2 border-transparent hover:border-[#fad24b] transition-all duration-300 text-left flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={resolveImageUrl(pick.image)}
                  alt={pick.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-3 left-3 flex items-center gap-1 bg-[#fad24b] text-[#1e1919] px-2 py-1 rounded-full text-xs font-bold">
                  <Flame className="w-3 h-3" />
                  {language === 'en' ? 'HOT' : 'TOP'}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              </div>
              <div className="p-4 flex flex-col flex-1">
                <span className="text-xs font-semibold text-[#fad24b] uppercase tracking-wide">
                  {pick.tag}
                </span>
                <h3 className="text-white font-bold mt-1 truncate">{pick.name}</h3>
                {pick.price && (
                  <p className="text-lg font-extrabold text-[#3dd3ee] mt-1" data-testid={`trending-pick-price-${index}`}>
                    {pick.price}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => orderOnWhatsApp(pick)}
                  data-testid={`trending-pick-order-${index}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] hover:bg-[#1eb257] text-white font-semibold py-2 transition-colors"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {language === 'en' ? 'Order on WhatsApp' : 'Ordenar por WhatsApp'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrendingPicks;
